import cn from 'classnames'
import { CSSProperties, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { getCockpitFiscalYearLabel } from '../../../../lib'
import { getOfferingTitleKey } from '../../../../lib/getOfferingTitleKey'
import type { ICockpitPlanningStatus } from '../../../../model/hooks'
import {
  selectCockpitOffering,
  selectEffectiveCockpitFYear,
  selectIsCockpitOfferingSwitchedOff,
} from '../../../../model/selectors'
import { CockpitIconButton } from '../../../components/cockpitIconButton'
import { CockpitScopeChip } from '../../../components/cockpitScopeChip'
import { useCockpitScope } from '../../hooks/useCockpitScope'
import { CockpitCategoryChip } from '../cockpitCategoryChip'
import { PlanningStatusBlocks, PlanningStatusLine } from '../cockpitPlanningStatus'
import type { TPlanningStatusBlocksTourTargets } from '../cockpitPlanningStatus'
import { CockpitScopeValues } from '../cockpitScopeValues'
import type { TCockpitScopeValuesTourTargets } from '../cockpitScopeValues'

import { useDeckPeek } from './useDeckPeek'

import styles from './CockpitPlanDeck.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.scopeCard
const PLAN_DECK_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.planDeck

/** Air between the figures and the completeness popover under them, the arrow's room. */
const POPOVER_GAP = 12

/** The fiscal year, larger than any title: it changes the whole cockpit, so it is chosen from a
 *  list in one step. A past year says so and offers the way back to the latest one. */
const DeckYear = ({ tourId }: { tourId: string }) => {
  const scope = useCockpitScope()

  return (
    <div className={styles.year}>
      <CockpitScopeChip
        appearance="display"
        value={scope.fYearLabel}
        label="Fiscal Year"
        ariaLabel="Fiscal Year"
        options={scope.yearOptions}
        selectedKeys={[String(scope.fYear)]}
        onSelect={(key) => scope.selectYear(Number(key))}
        footnote={<Translation text={KEYS.reloadNote} span />}
        tourId={tourId}
      />
      <span className={styles.yearStatus}>
        <Translation text={scope.isLatestFYear ? KEYS.currentYear : KEYS.pastYear} span />
        {!scope.isLatestFYear && (
          <>
            <span aria-hidden="true"> · </span>
            <button
              type="button"
              className={styles.back}
              onClick={() => scope.selectYear(scope.latestFYear)}
            >
              <Translation
                text={KEYS.backToYear}
                replaceParams={[{ key: '{year}', value: scope.latestFYearLabel }]}
                span
              />
            </button>
          </>
        )}
      </span>
    </div>
  )
}

/** The `data-tour-id`s of the deck and its controls. Both cockpits render the deck and each has a
 *  tour of its own, so each passes its own ids. */
export type TCockpitPlanDeckTourTargets = TCockpitScopeValuesTourTargets &
  TPlanningStatusBlocksTourTargets &
  Record<'scopeBar' | 'scopePanel' | 'scopeFiscalYear' | 'categoryChip', string>

interface ICockpitPlanDeckProps {
  /** The selected plan's state: one partner's programme, or the partners' counts in the manager
   *  cockpit. */
  status: ICockpitPlanningStatus
  /** The programme tabs, after the year. */
  offerings: ReactNode
  /** Help & tools, at the far end of the top row. */
  tools: ReactNode
  /** Leads the top row, before the year: the button folding the page's filter column. */
  lead?: ReactNode
  /** Takes the state's place while the offering in view is switched off. */
  switchedOffNote?: ReactNode
  tourTargets: TCockpitPlanDeckTourTargets
  /** A tile of the board is expanded: the deck folds to its top row and one line of the plan's
   *  state, and unfolds over the board while the pointer rests on it. */
  folded?: boolean
  /** Opens the selected plan's quality review from its score; without it the score only reads. */
  onQualityClick?: () => void
  /** Opens the plan's acknowledgment from the caption of its figure. */
  onAcknowledgmentClick?: () => void
  /** What the completeness caption opens, in a popover under the figures. */
  completenessDetail?: ReactNode
}

/** How much of the plan is complete, as a share: a programme's percent, or `Overall`'s complete
 *  programmes over all of them. */
const getCompletedShare = ({ completeness }: ICockpitPlanningStatus) =>
  'percent' in completeness
    ? (completeness.percent ?? 0) / 100
    : completeness.total > 0
      ? completeness.count / completeness.total
      : 0

/**
 * The white deck over the board, holding what sets the view and what it shows first: the year and
 * the offering tabs on the top row (on a partner's cockpit, with the switch of the partner's
 * offerings after them); under
 * them the selected plan's state in large figures, with the category and the period, metric and
 * currency the board is measured in. An offering switched off says so in place of its state.
 *
 * While a tile is expanded the deck folds: the state shrinks to one line and the plan's completeness
 * runs along the deck's lower edge. Resting the pointer on it unfolds the whole deck over the board,
 * and the pin there keeps it unfolded in place until it is unpinned or the tile is collapsed.
 */
export const CockpitPlanDeck = ({
  status,
  offerings,
  tools,
  lead,
  switchedOffNote,
  tourTargets,
  folded = false,
  onQualityClick,
  onAcknowledgmentClick,
  completenessDetail,
}: ICockpitPlanDeckProps) => {
  const t = useSafetyTranslation()
  const selectedOffering = useSelector(selectCockpitOffering)
  const isSwitchedOff = useSelector(selectIsCockpitOfferingSwitchedOff)
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const scope = useCockpitScope()
  const [isPinned, setIsPinned] = useState(false)
  const isFolded = folded && !isPinned
  const peek = useDeckPeek(isFolded)

  /* The pin belongs to the expanded tile: collapsing it brings the deck back unpinned. */
  useEffect(() => {
    if (!folded) {
      setIsPinned(false)
    }
  }, [folded])

  /* Each unfolding counts the figures up again, as switching the offering does. */
  const [peekRound, setPeekRound] = useState(0)

  useEffect(() => {
    if (peek.isOpen) {
      setPeekRound((round) => round + 1)
    }
  }, [peek.isOpen])

  /* The completeness popover hangs under the figures, its arrow at the caption. */
  const rootRef = useRef<HTMLElement>(null)
  const stateRef = useRef<HTMLDivElement>(null)
  const [isCompletenessOpen, setIsCompletenessOpen] = useState(false)
  const [completenessTop, setCompletenessTop] = useState(0)
  /* Set while a press lands in the popover or a list opened from it, which React carries through
     its portal: the document hears the press after the deck does. */
  const isPressInPopover = useRef(false)

  useLayoutEffect(() => {
    const root = rootRef.current
    const state = stateRef.current

    if (!isCompletenessOpen || !root || !state) {
      return
    }

    const place = () =>
      setCompletenessTop(
        state.getBoundingClientRect().bottom - root.getBoundingClientRect().top + POPOVER_GAP
      )

    place()
    window.addEventListener('resize', place)

    return () => window.removeEventListener('resize', place)
  }, [isCompletenessOpen, isFolded])

  useEffect(() => {
    if (!isCompletenessOpen) {
      return
    }

    const handlePress = () => {
      if (!isPressInPopover.current) {
        setIsCompletenessOpen(false)
      }

      isPressInPopover.current = false
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsCompletenessOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePress)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePress)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isCompletenessOpen])

  const onCompletenessClick = completenessDetail
    ? () => setIsCompletenessOpen((value) => !value)
    : undefined

  const offeringName = t(getOfferingTitleKey(selectedOffering))
  const completedShare = isSwitchedOff ? 0 : Math.min(1, getCompletedShare(status))

  const renderStateRow = (blocksKey: string) => (
    <div ref={stateRef} className={styles.state}>
      {isSwitchedOff ? (
        switchedOffNote
      ) : (
        /* Keyed by the offering, so each switch counts the figures up afresh. */
        <PlanningStatusBlocks
          key={blocksKey}
          status={status}
          animated
          tourTargets={tourTargets}
          onQualityClick={onQualityClick}
          onAcknowledgmentClick={onAcknowledgmentClick}
          onCompletenessClick={onCompletenessClick}
          isCompletenessOpen={isCompletenessOpen}
        />
      )}
      <div className={styles.scope}>
        <CockpitCategoryChip appearance="stacked" tourId={tourTargets.categoryChip} />
        <CockpitScopeValues layout="track" tourTargets={tourTargets} />
      </div>
    </div>
  )

  const pinLabel = t(isPinned ? PLAN_DECK_KEYS.unpin : PLAN_DECK_KEYS.pin)

  return (
    <section
      ref={rootRef}
      className={cn(styles.root, {
        [styles.folded]: isFolded,
        [styles.peeking]: peek.isOpen,
        [styles.done]: completedShare >= 1,
      })}
      style={
        isFolded ? ({ '--deck-completed': `${completedShare * 100}%` } as CSSProperties) : undefined
      }
      data-tour-id={tourTargets.scopeBar}
      {...peek.handlers}
    >
      <div className={styles.top} data-tour-id={tourTargets.scopePanel}>
        {lead}
        <DeckYear tourId={tourTargets.scopeFiscalYear} />
        <span className={styles.rule} aria-hidden="true" />
        <div className={styles.offerings}>{offerings}</div>
        <span className={styles.tools}>
          {/* Only an unfolded deck over an expanded tile has something to pin or unpin. */}
          {folded && (isPinned || peek.isOpen) && (
            <CockpitIconButton
              icon="kbase kbase-pushpin"
              label={pinLabel}
              pressed={isPinned}
              onClick={() => {
                setIsPinned((value) => !value)
                peek.close()
              }}
            />
          )}
          {tools}
        </span>
      </div>

      {isFolded ? (
        <div className={styles.fold}>
          <div className={styles.line}>
            <span className={styles.lineLead}>
              {isSwitchedOff ? (
                <Translation
                  text={PLAN_DECK_KEYS.switchedOff}
                  replaceParams={[
                    { key: '{offering}', value: offeringName },
                    { key: '{year}', value: getCockpitFiscalYearLabel(fYear) },
                  ]}
                  span
                />
              ) : (
                <>
                  <Translation
                    text={PLAN_DECK_KEYS.planOf}
                    replaceParams={[{ key: '{offering}', value: offeringName }]}
                    span
                  />
                  <PlanningStatusLine status={status} onQualityClick={onQualityClick} />
                </>
              )}
            </span>
            <button type="button" className={styles.lineScope} onClick={peek.open}>
              {[scope.periodLabel, scope.metric, scope.currency].filter(Boolean).join(' · ')}
              <i className="kbase kbase-chevron-bottom" aria-hidden />
            </button>
          </div>
          {/* Kept in place while folded, so it unfolds and folds back as a movement rather than
              appearing; folded away it takes no pointer, focus or reading. */}
          <div
            className={cn(styles.peek, { [styles.peekOpen]: peek.isOpen })}
            inert={!peek.isOpen || undefined}
            aria-hidden={!peek.isOpen || undefined}
          >
            {renderStateRow(`${selectedOffering}-${peekRound}`)}
          </div>
        </div>
      ) : (
        renderStateRow(selectedOffering)
      )}
      {isCompletenessOpen && completenessDetail && (
        <div
          className={styles.completenessPopover}
          style={{ top: completenessTop }}
          role="dialog"
          aria-label={t(PLAN_DECK_KEYS.completeness)}
          onPointerDownCapture={() => {
            isPressInPopover.current = true
          }}
        >
          {completenessDetail}
        </div>
      )}
    </section>
  )
}

CockpitPlanDeck.displayName = 'CockpitPlanDeck'
